import {AuthenticatedSessionState, type AuthenticatedSessionSnapshot} from "auth/AuthenticatedSessionWaiter";
import {listsUploadSessionsDatabase} from "core/upload/resume/uploadSessionsDatabase";

/**
 * Forgets the unfinished uploads an account kept in this browser, as it leaves it (kitecorp/transferify-ui#3204):
 * signing out, or deleting the account. Their keys, recipients and file copies are the account's, and the next
 * person at this browser must find none of them. The runtime is loaded on demand, and a failure is swallowed:
 * leaving never fails on storage, and anything left is ended past its deadline the next time the app opens.
 */
/**
 * Whose kept uploads a page may act on: the signed-in sender, null for nobody, and undefined while the session is
 * still being restored, when a signed-in sender would read as nobody and their own uploads past saving must be
 * ended on the api by them, not merely forgotten here.
 */
export function keptUploadsOwnerOf(session: AuthenticatedSessionSnapshot): string | null | undefined {
	if (session.state === AuthenticatedSessionState.Pending) {
		return undefined;
	}
	return session.state === AuthenticatedSessionState.Authenticated ? session.uid : null;
}

export function forgetKeptUploadsOf(session: AuthenticatedSessionSnapshot): Promise<void> {
	const leaving = session.state === AuthenticatedSessionState.Authenticated ? session.uid : null;
	if (leaving === null) {
		return Promise.resolve();
	}
	return import("core/upload/resume/resumeRuntime")
		.then(runtime => runtime.forgetUploadsOf(leaving))
		.catch(() => undefined);
}

/**
 * Ends the kept uploads past their deadline from a page the composer is not on; the composer ends them itself, and
 * says so once. The Cookie Policy promises they go the next time Transferify opens in this browser, wherever it
 * opens. Every page asks, so the runtime is loaded only when the browser may keep some: a visitor who never kept an
 * upload loads none of it, and gets nothing written. A failure is swallowed: the next page asks again.
 *
 * @param ownerUid  who is signed in, whose own uploads past saving are ended on the api too; null for nobody
 * @param indexedDb the IndexedDB to look in: the browser's own, read when called, unless a test hands it one
 */
export async function endKeptUploadsPastSaving(ownerUid: string | null,
											   indexedDb: () => IDBFactory | undefined = browserIndexedDb): Promise<void> {
	const factory = indexedDb();
	if (factory === undefined || await listsUploadSessionsDatabase(factory) === false) {
		return;
	}
	await import("core/upload/resume/resumeRuntime")
		.then(runtime => runtime.endKeptUploadsPastSaving(ownerUid))
		.catch(() => undefined);
}

function browserIndexedDb(): IDBFactory | undefined {
	return typeof indexedDB === "undefined" ? undefined : indexedDB;
}
